import {getScoreCssColor, getScoreRanking} from './creditScoreUtils';
import utils, {CATU_BUREAU_CODE, EFX_BUREAU_CODE} from './utils';

import _isEmpty from 'lodash/isEmpty';
import dateUtils from './dateUtils';

export function buildCreditScoreData(
  scoreData: UnifiedCreditScoreModel,
  scoreDelta: ScoreDelta,
  scoreHistory: ScoreHistory[],
  localizedStringBundle: StringMap,
  langCode: string,
  bureau = EFX_BUREAU_CODE
): CreditScoreProvider | null {
  // If scoreData is empty or undefined, return null
  if (_isEmpty(scoreData)) return null;

  let scoreName = null;
  let percentage = null;

  // Extract data from UnifiedCreditScoreModel
  let score = utils.safeGet(scoreData, 'currentScore', 'NA');

  const isScoreValid = (scoreCheck: string | number) => {
    return scoreCheck !== 0 && scoreCheck !== 1 && scoreCheck !== 4 && scoreCheck !== 'NA' && scoreCheck !== null;
  };

  const date = utils.safeGet(scoreData, 'generatedDate', localizedStringBundle.CREDIT_SCORE_DATE_UNAVAILABLE);
  const formattedDate = dateUtils.convertDateToSlashFormat(date, langCode, '-');
  const scoreType = utils.safeGet(scoreData, 'scoreType', localizedStringBundle.CREDIT_SCORE_REPORT_UNAVAILABLE);

  // Convert scoreReasons from UnifiedCreditScoreModel format to the expected format
  const scoreReasons =
    scoreData.scoreReasons?.map((reason) => ({
      code: reason.code,
      description: reason.description,
      // Map 'effect' to 'creditScoreFactorEffect' for backward compatibility
      creditScoreFactorEffect: reason.effect,
    })) || [];

  // Handle TransUnion special case
  if (bureau === CATU_BUREAU_CODE) {
    scoreReasons.forEach((reason) => (reason.description = localizedStringBundle[`TU_SCORE_REASON_${reason.code}`]));
  }

  // Create scoreRanges array from currentRange

  const scoreRanges = [];
  if (scoreData.currentRange) {
    scoreRanges.push({
      low: scoreData.currentRange.low,
      high: scoreData.currentRange.high,
      name: scoreData.currentRange.rating,
    });
  }

  // Determine scoreName based on score range
  if (isScoreValid(score)) {
    // Use the rating from currentRange if available
    scoreName = scoreData.currentRange?.rating || null;
  } else {
    scoreName = 'NA'; // don't localize this because it is used as a key in getScoreRanking and getScoreCssColor
  }

  const scoreRangeIndex = getScoreRanking(scoreName);
  const cssColor = getScoreCssColor(scoreName);

  if (isScoreValid(score) && !_isEmpty(scoreData.overallRange)) {
    const overallLow = scoreData.overallRange.low;
    const overallHigh = scoreData.overallRange.high;
    const overallPercentage = ((score - overallLow) * 100) / (overallHigh - overallLow);
    percentage = overallPercentage.toFixed(0);
  } else {
    percentage = 0;
    if (score === 'NA') {
      score = localizedStringBundle.CREDIT_SCORE_DASH;
    }
  }

  return {
    score,
    percentage,
    date,
    formattedDate,
    scoreType,
    scoreReasons,
    scoreRanges,
    scoreName,
    cssColor,
    scoreRangeIndex,
    provider: scoreData.provider,
    scoreDelta,
    scoreHistory,
  };
}
